import { useMemo } from "react";
import client from "libs/client";
import type { continentCountries } from "pages/productManagement/types";

export function useSubscriptionFormOptions() {
	const { data: productsConfig, isLoading: isProductsConfigLoading } = client.getSubscriptionProductsConfig.useQuery({
		retry: false,
		select: (response) => response.data,
	});
	const { data: continentData, isLoading: isContinentsLoading } = client.getContinentCountry.useQuery({}, { retry: false });
	const { data: ownershipData, isLoading: isOwnershipsLoading } = client.loadOwnerships.useQuery({}, { retry: false });
	const { data: teamGroupsData, isLoading: isTeamGroupsLoading } = client.loadTeamGroups.useQuery({}, { retry: false });
	const { data: competitionsData, isLoading: isCompetitionsLoading } = client.loadSubscriptionCompetitions.useQuery(
		{},
		{ retry: false },
	);
	const { data: competitionEditionsData, isLoading: isCompetitionEditionsLoading } =
		client.loadSubscriptionCompetitionEditions.useQuery({}, { retry: false });
	const { data: eyeballAddonsData, isLoading: isEyeballAddonsLoading } = client.getAllEyeballAddons.useQuery({}, { retry: false });
	const { data: discoverCountriesData, isLoading: isDiscoverCountriesLoading } =
		client.getSubscriptionDiscoverCountries.useQuery({}, { retry: false });

	const countriesWithContinents = useMemo((): continentCountries[] => {
		return (continentData?.data as continentCountries[] | undefined) ?? [];
	}, [continentData]);

	const subscriptionProducts = useMemo(() => {
		return (
			productsConfig?.products.map((item) => ({
				value: item.value ?? "",
				label: item.label,
			})) ?? []
		);
	}, [productsConfig]);

	const ownershipOptions = useMemo(() => {
		return ownershipData?.data?.data ?? [];
	}, [ownershipData]);

	const teamGroupOptions = useMemo(() => {
		return teamGroupsData?.data?.data ?? [];
	}, [teamGroupsData]);

	const competitionOptions = useMemo(() => {
		return (
			competitionsData?.data?.data
				?.filter((item) => item.value != null)
				.map((item) => ({
					value: Number(item.value),
					label: String(item.label ?? item.value),
				})) ?? []
		);
	}, [competitionsData]);

	const competitionEditionOptions = useMemo(() => {
		return (
			competitionEditionsData?.data?.data
				?.filter((item) => item.value != null)
				.map((item) => ({
					value: Number(item.value),
					label: String(item.label ?? item.value),
				})) ?? []
		);
	}, [competitionEditionsData]);

	const eyeballAddons = useMemo(() => {
		return (
			eyeballAddonsData?.data?.map((item) => ({
				value: String(item.value),
				label: String(item.label),
			})) ?? []
		);
	}, [eyeballAddonsData]);

	const discoverCountryOptions = useMemo(() => {
		return (
			discoverCountriesData?.data?.map((item) => ({
				value: String(item.value),
				label: String(item.label),
			})) ?? []
		);
	}, [discoverCountriesData]);

	const isLoading =
		isProductsConfigLoading ||
		isContinentsLoading ||
		isOwnershipsLoading ||
		isTeamGroupsLoading ||
		isCompetitionsLoading ||
		isCompetitionEditionsLoading ||
		isEyeballAddonsLoading ||
		isDiscoverCountriesLoading;

	return {
		productsConfig,
		countriesWithContinents,
		subscriptionProducts,
		ownershipOptions,
		teamGroupOptions,
		competitionOptions,
		competitionEditionOptions,
		eyeballAddons,
		discoverCountryOptions,
		isLoading,
	};
}

export default useSubscriptionFormOptions;
